// @ts-check
/** Collect the balance (or any correction) on an already-created order - one payment at a
 * time, in whatever method it actually came in. */
import { useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminOrderService from '../services/adminOrderService';
import { LEDGER_PAYMENT_METHODS } from '../utils/posOrder';
import Modal from './ui/Modal';

const input = 'w-full px-3 py-3 text-base border border-input rounded-lg';
const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

/** @param {{orderId: string, balanceDue: number, onClose: () => void, onRecorded: () => void}} props */
const RecordPaymentModal = ({ orderId, balanceDue, onClose, onRecorded }) => {
  const toast = useToast();
  const [amount, setAmount] = useState(balanceDue > 0 ? balanceDue : 0);
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const overpaying = amount > balanceDue;

  const handleSave = async () => {
    if (!(amount > 0)) {
      toast.error('Enter an amount above zero');
      return;
    }
    if (overpaying) {
      const confirmMessage =
        balanceDue < 0
          ? `This order is already overpaid by ${inr(Math.abs(balanceDue))}. Recording ${inr(amount)} more will increase that. Continue?`
          : `This is ${inr(amount - balanceDue)} more than the ${inr(balanceDue)} balance due. Continue?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    setSaving(true);
    try {
      await adminOrderService.recordPayment(orderId, {
        amount,
        method,
        reference: reference.trim() || null,
        allow_overpayment: overpaying,
      });
      toast.success('Payment recorded');
      onRecorded();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to record the payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="sm" onClose={onClose} hasUnsavedChanges={amount !== balanceDue}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">Record a payment</h2>
        <p className="text-sm text-muted-foreground mt-1">Balance due: {inr(balanceDue)}</p>
      </div>
      <div className="px-6 py-4 space-y-3">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          placeholder="Amount *"
          value={amount || ''}
          onChange={(e) => setAmount(Number(e.target.value) || 0)}
          className={input}
        />
        <select value={method} onChange={(e) => setMethod(e.target.value)} className={input}>
          {LEDGER_PAYMENT_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <input
          placeholder="Reference (optional)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className={input}
        />
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-3 text-base bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {saving ? 'Saving...' : 'Record payment'}
        </button>
        <button
          onClick={onClose}
          disabled={saving}
          className="px-4 py-3 text-base border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default RecordPaymentModal;
